import React from 'react';
import {Holiday, Person, Week, Weeks} from './TimesheetsCalendarTypes';
import {
	addDays,
	endOfMonth,
	endOfWeek,
	getWeek,
	isAfter,
	isBefore,
	isSameDay,
	isWeekend,
	startOfMonth,
	startOfWeek,
} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {CalendarEventType} from './calendar-events/CalendarEventsFetch';
import Util from '../../../shared/util/util';
import * as tracking from '../../../../tracking';
import RevokeGoogleCalendarTokenMutation from '../../../../mutations/revoke_google_calendar_token_mutation';

export const DAY_PREFIX = 'day-';
export const WEEK_PREFIX = 'week-';

export const LOCALSTORAGE_DISMISS_KEY = 'calendar-info-dismiss-count';
export const LOCALSTORAGE_TIMESHEETS_SELECTED_TAB = 'timesheets_selected_tab';

export const onKeyboardEventHandler = (event: React.KeyboardEvent, onEnter: () => void): void => {
	switch (event.key) {
		case 'Enter':
			onEnter();
			break;
		default:
			break;
	}
};

export const getHolidaysOfTheDay = (person: Person, day: Date): Holiday[] => {
	return person.holidayCalendar
		? person.holidayCalendar.holidayCalendarEntries.edges.filter(
				d => d.node.day === day.getDate() && d.node.month === day.getMonth() + 1 && d.node.year === day.getFullYear()
		  )
		: [];
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// A calendar day as a plain YYYYMMDD number, so two days can be ordered without either of
// them first becoming an instant.
const toCalendarDay = (date: Date): number => date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

// The person's own dates arrive as date-only strings ("YYYY-MM-DD") while the calendar builds
// its cells as local Date objects, and `new Date('2026-08-24')` is UTC midnight -- which sorts
// *before* the local midnight of that same day everywhere east of UTC. That is what made a
// person's own start date render as a zero-hour, greyed-out cell for every viewer in a UTC+
// timezone (FORECAST-177), so the comparison stays in calendar-day space instead.
// `createdAt` is a timestamp rather than a date-only string; only the day it fell on matters
// here, which is also why a person created at 09:00 no longer greys out their first day.
export const personDateToCalendarDay = (personDate: string): number => {
	const dateOnly = DATE_ONLY_PATTERN.exec(personDate);
	return dateOnly ? Number(dateOnly[1] + dateOnly[2] + dateOnly[3]) : toCalendarDay(new Date(personDate));
};

export const workingHourForTheDay = (person: Person, day: Date): number => {
	const personStartDay = personDateToCalendarDay(person.startDate || person.createdAt);
	const personEndDay = person.endDate ? personDateToCalendarDay(person.endDate) : null;
	const calendarDay = toCalendarDay(day);

	// The end date itself counts as not worked, which is what this calendar has always shown.
	if (calendarDay < personStartDay || (personEndDay !== null && calendarDay >= personEndDay)) {
		return 0;
	}

	const workingHours = [
		person.sunday,
		person.monday,
		person.tuesday,
		person.wednesday,
		person.thursday,
		person.friday,
		person.saturday,
	];

	return workingHours[day.getDay()];
};

export const isBetween = (day: Date, first: Date, last: Date): boolean => {
	return (isBefore(day, last) || isSameDay(day, last)) && (isAfter(day, first) || isSameDay(day, first));
};

// A week's target is just its days' targets: the same start-date, end-date and weekday rules,
// applied seven times. Deriving it here separately meant the boundaries were applied twice, in
// two different ways, and the second one was wrong -- it indexed the Sunday-to-Saturday
// working-hours array with getDate(), the day of the *month*, so a person starting on the 3rd
// had Sunday, Monday and Tuesday zeroed no matter which weekday they actually started on.
export const workingHourForTheWeek = (person: Person, lastDayOfWeek: Date): number => {
	const firstDayOfWeek = startOfWeek(lastDayOfWeek);

	return [0, 1, 2, 3, 4, 5, 6].reduce(
		(total, dayOfWeek) => total + workingHourForTheDay(person, addDays(firstDayOfWeek, dayOfWeek)),
		0
	);
};

const getWeekDateArray = (weekStart: Date, hideWeekend: boolean) => {
	const includedDays = hideWeekend ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6];
	const week: Date[] = [];

	for (let n = 0; n < 7; n++) {
		const day = addDays(new Date(weekStart.getTime()), n);
		if (includedDays.includes(day.getDay())) {
			week.push(day);
		}
	}

	return week;
};

export const getWeeksOfMonth = (currentViewingMonth: Date, hideWeekend: boolean): Weeks => {
	let startDate = startOfWeek(startOfMonth(currentViewingMonth));
	const endDate = endOfWeek(endOfMonth(currentViewingMonth));

	const calendar: Weeks = [];
	while (isBefore(startDate, endDate)) {
		const weekStart = startDate;
		calendar.push({
			days: getWeekDateArray(weekStart, hideWeekend),
		});
		startDate = addDays(startDate, 7);
	}

	return calendar;
};

export const isDaySelected = (selectedDate: string, day: Date): boolean => {
	return selectedDate === DAY_PREFIX + day.getDate() + '-' + day.getMonth();
};

export const isWeekSelected = (selectedDate: string, week: Week): boolean => {
	const day = week.days[0];
	return selectedDate === WEEK_PREFIX + getWeek(day);
};

export const getFirstDayOfWeek = (): number => {
	return startOfWeek(new Date()).getDay();
};

export const getWeekdaysShort = (hideWeekend: boolean): string[] => {
	const days: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const result: string[] = [];
	let day = startOfWeek(new Date());
	for (let n = 0; n < 7; n++) {
		if (!(isWeekend(day) && hideWeekend)) {
			result.push(days[day.getDay()]);
		}
		day = addDays(day, 1);
	}
	return result;
};

export const getStartDate = (event: CalendarEventType | undefined): Date | null => {
	return event?.start && event?.calendarTimeZone ? utcToZonedTime(event.start, event.calendarTimeZone) : null;
};

export const getEndDate = (event: CalendarEventType | undefined): Date | null => {
	return event?.end && event?.calendarTimeZone ? utcToZonedTime(event.end, event.calendarTimeZone) : null;
};

export const revokeTokens = (personId: string): void => {
	tracking.trackEvent('Google Calendar Revoke Tokens');
	localStorage.removeItem(LOCALSTORAGE_TIMESHEETS_SELECTED_TAB);
	localStorage.setItem(LOCALSTORAGE_DISMISS_KEY, '0');
	Util.CommitMutation(RevokeGoogleCalendarTokenMutation, {
		personId: personId,
	});
};
